import type { PortalData } from '../generated/api';
import config from '../config';
import LocalStorage from './LocalStorage';

export const PORTAL_DATA_KEY = 'portal-data';
const TTL = 1000 * 60 * 60 * 24 * 15; //15 days

function now() {
  return new Date().getTime();
}

async function warm() {
  console.log('WARMING UP portal-data');
  // An entry without tenant_id (cached from an unknown-host response) is
  // useless for the 15-day TTL — treat it as a miss and refetch.
  const current = LocalStorage.get(LocalStorage.Keys.PortalData);
  if (current && current.exp >= now() && current.tenant_id) {
    console.log('CACHE-HIT', current);
    return current;
  }

  console.log('CACHE-MISS');
  const url = `${config.PROTOCOL}://${config.API_URL}/api/portal-data`;
  let data: PortalData;

  try {
    data = await (await fetch(url)).json();
  } catch (err) {
    console.log('WARMUP failed');
    return;
  }

  const exp = now() + TTL;
  LocalStorage.set(LocalStorage.Keys.PortalData, { ...data, exp });
  console.log('SAVING', data);
  return data;
}

/**
 * Resolves once portal-data is available (from cache or the network) and the
 * tenant_id API header has been set. Anonymous pages that call the API on
 * mount (e.g. /referral-submission) must await this before their first
 * request, or the request goes out without a tenant and gets a 400.
 */
export const portalDataReady: Promise<PortalData | undefined> = warm().then(async (data) => {
  if (data?.tenant_id) {
    // The redux header updater only re-runs on store changes, so a warmup
    // that finishes after the initial render would never reach the API
    // headers — push it directly. Imported lazily because ApiThunks depends
    // on this module (via selectors) and a static import would be a cycle.
    const { updateApiHeaders } = await import('careand-redux/api/ApiThunks');
    updateApiHeaders({ tenant_id: data.tenant_id });
  }
  return data;
});

(window as any).careand_warmup = portalDataReady;

export const getPortalData = () => LocalStorage.get(LocalStorage.Keys.PortalData);
